import Gleap from 'gleap';

type GleapEnvironment = 'dev' | 'staging' | 'prod';

type GleapContact = Parameters<typeof Gleap.identify>[1];
type GleapCustomDataValue = string | number | boolean | null | undefined;
type GleapCustomData = Record<string, GleapCustomDataValue>;
export type GleapBooleanTrait =
  | 'has_camera'
  | 'has_deployment'
  | 'has_gateway'
  | 'has_input_streams'
  | 'has_nonempty_pipeline';

let isInitialized = false;
let hasWarnedAboutMissingKey = false;
let identifiedUserId: string | null = null;
let currentWorkspaceId: string | null = null;
let currentCustomData: Record<string, string | number | boolean> = {};

function clearGleapContext() {
  currentWorkspaceId = null;
  currentCustomData = {};

  if (!isInitialized) {
    return;
  }

  Gleap.clearCustomData();
  Gleap.clearTicketAttributes();
}

function getGleapEnvironment(): GleapEnvironment {
  if (import.meta.env.MODE === 'production') {
    return 'prod';
  }

  if (import.meta.env.MODE === 'staging') {
    return 'staging';
  }

  return 'dev';
}

export function initializeGleap() {
  const apiKey = import.meta.env.VITE_GLEAP_API_KEY;

  if (!apiKey) {
    if (!hasWarnedAboutMissingKey) {
      console.warn('Gleap is disabled because VITE_GLEAP_API_KEY is not set.');
      hasWarnedAboutMissingKey = true;
    }

    return false;
  }

  if (isInitialized) {
    return true;
  }

  Gleap.initialize(apiKey);
  Gleap.setEnvironment(getGleapEnvironment());
  Gleap.showFeedbackButton(false);
  isInitialized = true;
  applyGleapTicketContext();

  return true;
}

function applyGleapTicketContext() {
  if (!currentWorkspaceId) {
    return;
  }

  Gleap.setTicketAttribute('lumeo_workspace_id', currentWorkspaceId);
}

function normalizeCustomData(customData: GleapCustomData) {
  return Object.fromEntries(
    Object.entries(customData).filter(
      (entry): entry is [string, string | number | boolean] =>
        entry[1] !== null && entry[1] !== undefined
    )
  );
}

function getBooleanTraitStorageKey(
  userId: string,
  workspaceId: string,
  trait: GleapBooleanTrait
) {
  return `gleap:boolean-trait:${userId}:${workspaceId}:${trait}`;
}

function readStoredBooleanTrait(key: string) {
  try {
    const value = localStorage.getItem(key);

    if (value === null) {
      return null;
    }

    return value === 'true';
  } catch {
    return null;
  }
}

function writeStoredBooleanTrait(key: string, value: boolean) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Ignore storage failures; tracking should never block the UI.
  }
}

export function setGleapWorkspaceContext(workspaceId: string) {
  currentWorkspaceId = workspaceId;

  if (!initializeGleap()) {
    return;
  }

  applyGleapTicketContext();
}

export function resetGleapIdentity() {
  clearGleapContext();

  if (isInitialized) {
    Gleap.clearIdentity();
  }

  identifiedUserId = null;
}

export function updateGleapCustomData(customData: GleapCustomData) {
  const nextCustomData = normalizeCustomData(customData);

  if (Object.keys(nextCustomData).length === 0) {
    return;
  }

  currentCustomData = {
    ...currentCustomData,
    ...nextCustomData,
  };

  if (!initializeGleap()) {
    return;
  }

  Gleap.attachCustomData(nextCustomData);

  if (identifiedUserId) {
    Gleap.updateContact({ customData: currentCustomData });
  }
}

export function trackGleapBooleanTraitEnabled({
  userId,
  workspaceId,
  trait,
  value,
}: {
  userId: string;
  workspaceId: string;
  trait: GleapBooleanTrait;
  value: boolean;
}) {
  const storageKey = getBooleanTraitStorageKey(userId, workspaceId, trait);
  const previousValue = readStoredBooleanTrait(storageKey);

  writeStoredBooleanTrait(storageKey, value);

  if (previousValue !== false || !value) {
    return;
  }

  if (!initializeGleap()) {
    return;
  }

  Gleap.trackEvent(`${trait}_changed_to_true`, {
    trait,
    previous_value: false,
    current_value: true,
    application_id: workspaceId,
  });
}

export function identifyGleapContact(userId: string, contact: GleapContact) {
  if (identifiedUserId && identifiedUserId !== userId) {
    resetGleapIdentity();
  }

  if (!initializeGleap()) {
    return;
  }

  const contactCustomData =
    contact.customData && typeof contact.customData === 'object'
      ? normalizeCustomData(contact.customData as GleapCustomData)
      : {};
  const mergedContact = {
    ...contact,
    customData: {
      ...currentCustomData,
      ...contactCustomData,
    },
  };

  currentCustomData = mergedContact.customData;

  if (identifiedUserId === userId) {
    Gleap.updateContact(mergedContact);
    return;
  }

  Gleap.identify(userId, mergedContact);
  identifiedUserId = userId;
}

export function showGleap() {
  if (!initializeGleap()) {
    return;
  }

  applyGleapTicketContext();
  Gleap.open();
}

export function showGleapNewConversation(prefill?: string) {
  if (!initializeGleap()) {
    return;
  }

  applyGleapTicketContext();

  if (prefill) {
    Gleap.preFillForm({
      description: prefill,
      message: prefill,
    });
  }

  Gleap.startConversation();
}
